import { z } from 'zod';
import { BODY_FORMATS } from '../lib/body';

/**
 * A template, customized for one wedding.
 *
 * The problem this solves: a planner picks the "Florist — first contact"
 * template for the Nguyễn wedding, and it is nearly right. The couple want
 * something said about the ceremony being outdoors; the venue has a loading
 * restriction every florist needs to know. Before this existed, the only place
 * to put that was inside each of the eight individual messages, one at a time,
 * after the composer had already rendered them — the same sentence typed eight
 * times, with eight chances to typo it.
 *
 * So the edit lands in between: below the saved template, above the individual
 * messages. It belongs to `(wedding, category)` rather than to the template,
 * because the thing being described is *this wedding*, and the template is
 * reused across every wedding the planner will ever run. Writing it back to
 * the template would leak one couple's ceremony into the next couple's
 * inquiries — silently, months later, which is the worst possible way to find
 * out.
 *
 * `templateId` records which template the override was derived from. It is
 * kept so the composer can say "this no longer matches the template you picked"
 * rather than silently sending a customization built on something else.
 */
export const saveTemplateOverrideSchema = z.object({
  templateId: z.string().uuid().nullable().optional(),
  subject: z.string().min(1).max(255),
  body: z.string().min(1).max(60000),
  bodyFormat: z.enum(BODY_FORMATS).default('text'),
});

export type SaveTemplateOverrideInput = z.infer<typeof saveTemplateOverrideSchema>;
